import { useDispatch } from 'react-redux';

import downloadSvg from 'assets/icons/download.svg';
import { PermissionsEnum } from 'consts/permissions';
import Auth from 'modules/Auth';
import { downloadSetupFileV2 } from 'modules/api/setup';
import { addToastNotification } from 'store/notification/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { download } from 'utils/download';
import { SetupFrameInformation } from 'components/pages/InventoryManagement/Setup/Setup.types';
import Button, { Color } from 'lib/Button';

const FileNameRenderer = ({
  data,
  isAssetTemplateV2Enabled,
}: {
  data: SetupFrameInformation;
  isAssetTemplateV2Enabled: boolean;
}): React.ReactElement | null => {
  const dispatch = useDispatch();

  if (!data?.fileName) return null;

  const canDownload = isAssetTemplateV2Enabled && Auth.hasPermission(PermissionsEnum.SETUP_DOWNLOAD);

  if (!canDownload) {
    return <span>{data.fileName}</span>;
  }

  const handleDownloadFile = async (): Promise<void> => {
    try {
      const response = await downloadSetupFileV2(cancelFunctions, data.fileId);

      if (!response.data) {
        dispatch(
          addToastNotification({
            description: `File ${data.fileName} is not available`,
            type: 'Warning',
            timeout: 10000,
          }),
        );
        return;
      }

      download(new Blob([response.data]), data.fileName);
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;

      if (status === 404) {
        dispatch(
          addToastNotification({
            description: `File ${data.fileName} is not available`,
            type: 'Warning',
            timeout: 10000,
          }),
        );
        return;
      }

      dispatch(
        addToastNotification({
          description: `Failed to download ${data.fileName}. Please try again.`,
          type: 'Failure',
          timeout: 10000,
        }),
      );
    }
  };

  return (
    <Button
      classNames="group/download flex-row-reverse !text-essential-primary hover:!text-primary-600 hover:!bg-primary-200 w-full max-w-full"
      color={Color.TRANSPARENT}
      onClick={handleDownloadFile}
      label={data.fileName}
      svg={downloadSvg}
      svgClassnames="invisible group-hover/download:visible"
    />
  );
};

export default FileNameRenderer;
